import { useEffect, useMemo, useState } from "react";
import {
  Text,
  TableContainer,
  Table,
  Thead,
  Tr,
  Th,
  Flex,
  useBreakpointValue,
  Tbody,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Td,
  AccordionIcon,
  Box,
  Badge,
  HStack,
  VStack,
  Icon,
  Avatar,
  IconButton,
} from "@chakra-ui/react";
import { FiPackage, FiTool, FiSettings, FiZap, FiRefreshCw } from "react-icons/fi";
import Link from "../../../../components/Link";
import urlToId from "../../../../helpers/UrlToId";
import { Card, CardHeader, CardTitle, CardContent } from "../../../../components/Card"
import { ServiceState } from "../../../../components/States";
import { translateType } from "../../../../shared/libs/translate";
import { useRevalidator } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import {
  Right,
} from "../../../../components/Card";
export function ServicesTableInner({ services, onRefresh }) {
  const { t } = useTranslation();
  const bgGradient = "linear(135deg, white 0%, gray.50 100%)";
  const headerBg = "linear(135deg, teal.500 0%, green.600 100%)";
  const cardBg = "white";
  const tableBg = "gray.50";
  const rowHoverBg = "teal.50";
  const textColor = "gray.700";
  const borderColor = "gray.200";
  const accordionBg = "white";
  const accordionBorderColor = "gray.200";
  const accordionButtonBg = "gray.50";
  const accordionButtonHoverBg = "gray.100";
  const accordionIconColor = "gray.600";
  const tableHeaderBg = "gray.100";
  const tableBorderColor = "gray.200";
  const tableRowHoverBg = "blue.50";
  const tableRowBorderColor = "gray.200";
  const descriptionTextColor = "gray.600";

  const columns = [
    {
      title: t('service.unit'),
      dataIndex: "project_name",
      render: (text, record) => (
        <HStack spacing={4}>
          <Avatar
            size="md"
            name={text}
            bg="teal.500"
            color="white"
            icon={<FiPackage />}
          />
          <VStack align="start" spacing={1}>
            <Badge
              colorScheme="teal"
              variant="subtle"
              borderRadius="full"
              px={4}
              py={2}
              fontSize="sm"
              fontWeight="bold"
            >
              {text}
            </Badge>
            <HStack spacing={2}>
              <Icon as={FiSettings} color="gray.500" boxSize={3} />
              <Text fontSize="sm" fontWeight="bold" color={textColor}>
                {t('service.unit_label')}: {record.apt_num}
              </Text>
            </HStack>
        </VStack>
      </HStack>
      ),
    },
    {
      title: t('service.faulty_products'),
      dataIndex: "service_records",
      render: (services) => (
        <Box
          bg={accordionBg}
          borderRadius="lg"
          border="1px"
          borderColor={accordionBorderColor}
          shadow="md"
          overflow="hidden"
        >
          <Accordion allowMultiple>
            <AccordionItem border={0}>
              <AccordionButton
                bg={accordionButtonBg}
                _hover={{ bg: accordionButtonHoverBg }}
                py={4}
                px={6}
              >
                <HStack flex={1} spacing={3}>
                  <Icon as={FiTool} color="orange.500" boxSize={5} />
                  <VStack align="start" spacing={0}>
                    <Text fontWeight="bold" color={textColor}>
                      {services
                        ? t('service.faulty_products_count', { count: services.length })
                        : t('service.no_service_record')}
                    </Text>
                    <Text fontSize="sm" color="gray.500">
                      {t('service.click_for_details')}
                    </Text>
                  </VStack>
                </HStack>
                <AccordionIcon color={accordionIconColor} />
              </AccordionButton>
              <AccordionPanel p={0}>
                <TableContainer>
                  <Table variant="simple" size="sm">
                    <Thead bg={tableHeaderBg}>
                      <Tr>
                        <Th borderColor={tableBorderColor}>
                          <HStack spacing={2}>
                            <Text fontSize="xs" fontWeight="bold" textTransform="uppercase">
                              #
                            </Text>
                          </HStack>
                        </Th>
                        <Th borderColor={tableBorderColor}>
                          <HStack spacing={2}>
                            <Icon as={FiPackage} boxSize={3} color="blue.500" />
                            <Text fontSize="xs" fontWeight="bold" textTransform="uppercase">
                              {t('service.device')}
                            </Text>
                          </HStack>
                        </Th>
                        <Th borderColor={tableBorderColor}>
                          <Text fontSize="xs" fontWeight="bold" textTransform="uppercase">
                            {t('service.type')}
                          </Text>
                        </Th>
                        <Th borderColor={tableBorderColor}>
                          <Text fontSize="xs" fontWeight="bold" textTransform="uppercase">
                            {t('service.description')}
                          </Text>
                        </Th>
                        <Th borderColor={tableBorderColor}>
                          <Text fontSize="xs" fontWeight="bold" textTransform="uppercase">
                            {t('service.status')}
                          </Text>
                        </Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {services &&
                        services.map((service) => (
                          <Tr
                            key={service.url}
                            _hover={{
                              bg: tableRowHoverBg,
                              transform: "translateX(4px)"
                            }}
                            transition="all 0.2s ease"
                            borderBottom="1px"
                            borderColor={tableRowBorderColor}
                          >
                            <Td py={3}>
                              <Badge
                                colorScheme="purple"
                                variant="outline"
                                borderRadius="full"
                              >
                                {service.number}
                              </Badge>
                            </Td>
                            <Td py={3}>
                              <Link
                                to={`/servis/${urlToId(service.url) || ""}`}
                              >
                                <Text
                                  color="blue.500"
                                  fontWeight="semibold"
                                  _hover={{ color: "blue.600", textDecoration: "underline" }}
                                  transition="color 0.2s ease"
                                >
                                  {service.product_model.name}
                                </Text>
                              </Link>
                            </Td>
                            <Td py={3}>
                              <Badge
                                colorScheme="green"
                                variant="subtle"
                                borderRadius="md"
                              >
                                {translateType(service.service_type)}
                              </Badge>
                            </Td>
                            <Td py={3}>
                              <Text
                                fontSize="sm"
                                color={descriptionTextColor}
                                noOfLines={2}
                              >
                                {service.desc}
                              </Text>
                            </Td>
                            <Td py={3}>
                              <ServiceState state={service.state} />
                            </Td>
                          </Tr>
                        ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </Box>
      ),
    },
  ];
  return (
    <Box
      bgGradient={bgGradient}
      borderRadius="2xl"
      p={6}
      shadow="2xl"
      border="1px"
      borderColor={borderColor}
    >
      {/* Modern Header */}
      <Box
        bgGradient={headerBg}
        borderRadius="xl"
        p={6}
        mb={6}
        shadow="lg"
      >
        <Flex justify="space-between" align="center">
          <HStack spacing={4}>
            <Icon as={FiZap} color="white" boxSize={6} />
            <VStack align="start" spacing={1}>
              <Text
                fontSize="2xl"
                fontWeight="bold"
                color="white"
                letterSpacing="tight"
              >
                {t('service.service_management')}
              </Text>
              <Text fontSize="sm" color="whiteAlpha.800">
                {t('service.active_service_records')}
              </Text>
            </VStack>
          </HStack>
          <IconButton
            aria-label={t('common.refresh')}
            icon={<FiRefreshCw />}
            onClick={onRefresh}
            variant="ghost"
            color="white"
            _hover={{ bg: "whiteAlpha.200" }}
            size="lg"
          />
        </Flex>
      </Box>

      {/* Content Area */}
      <Box
        bg={cardBg}
        borderRadius="xl"
        shadow="lg"
        overflow="hidden"
        border="1px"
        borderColor={borderColor}
      >
        {services && services.length > 0 ? (
          <TableContainer whiteSpace="wrap">
            <Table variant="simple" size="md">
              <Thead bg={tableBg}>
                <Tr>
                  {columns.map((column) => (
                    <Th
                      key={column.dataIndex}
                      py={4}
                      px={6}
                      borderBottom="2px"
                      borderColor={borderColor}
                    >
                      <Text
                        fontSize="sm"
                        fontWeight="bold"
                        color={textColor}
                        textTransform="uppercase"
                        letterSpacing="wide"
                      >
                        {column.title}
                      </Text>
                    </Th>
                  ))}
                </Tr>
              </Thead>
              <Tbody>
                {services.map((service, index) => (
                  <Tr
                    key={service.project_name + service.apt_num}
                    _hover={{
                      bg: rowHoverBg,
                      transform: "translateX(4px)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
                    }}
                    transition="all 0.3s ease"
                    borderBottom={index === services.length - 1 ? "none" : "1px"}
                    borderColor={borderColor}
                  >
                    {columns.map((column) => (
                      <Td
                        key={column.dataIndex}
                        py={4}
                        px={6}
                        position="relative"
                        _before={{
                          content: '""',
                          position: "absolute",
                          left: 0,
                          top: 0,
                          width: "3px",
                          height: "100%",
                          bg: "transparent",
                          transition: "all 0.3s ease"
                        }}
                        _hover={{
                          _before: {
                            bg: "teal.400"
                          }
                        }}
                      >
                        {column.render
                          ? column.render(service[column.dataIndex], service)
                          : service[column.dataIndex]}
                      </Td>
                    ))}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        ) : (
          <Box p={12} textAlign="center">
            <VStack spacing={4}>
              <Icon as={FiTool} boxSize={12} color="gray.400" />
              <Text fontSize="lg" color="gray.500" fontWeight="medium">
                {t('service.no_service_records_yet')}
              </Text>
              <Text fontSize="sm" color="gray.400">
                {t('service.new_work_orders_appear_here')}
              </Text>
            </VStack>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default function ServiceListTableView({ services }) {
  const revalidator = useRevalidator();
  const [searchTerm, setSearchTerm] = useState("");
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const q = params.get('q');
      if (q) setSearchTerm(q);
    } catch (_) {}
  }, []);
  const filteredServices = useMemo(() => {
    const arr = Array.isArray(services) ? services : [];
    const s = (searchTerm || "").toLowerCase();
    if (!s) return arr;
    return arr.filter(u => {
      const base = (u.project_name || "").toLowerCase().includes(s) || String(u.apt_num || "").toLowerCase().includes(s);
      const recs = Array.isArray(u.service_records) ? u.service_records : [];
      const any = recs.some(r => (
        (r.desc || "").toLowerCase().includes(s) ||
        (r.product_model?.name || "").toLowerCase().includes(s) ||
        String(r.number || "").toLowerCase().includes(s)
      ));
      return base || any;
    });
  }, [services, searchTerm]);
  
  const handleRefresh = () => {
    revalidator.revalidate();
  };
    const cardSpacing = useBreakpointValue({ base: 4, md: 6, lg: 8 });
  const { t } = useTranslation();

  return (
    <VStack spacing={cardSpacing} align="stretch">
      <Card>
  
     
           
          <ServicesTableInner services={filteredServices} onRefresh={handleRefresh} />
  
      </Card>
    </VStack>
  );
}
